import React, { useState } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FaRegCalendarAlt, FaArrowRight, FaFilter } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import { ParticlesBackground } from '../../components/ui';

const News = () => {
  const { t, lang } = useLanguage();
  const [filter, setFilter] = useState('all');

  const newsItems = t.newsPage?.items || [];

    // Tri du plus récent au plus ancien
  const sortedNews = [...newsItems].sort((a, b) =>
    (b.sortDate || '').localeCompare(a.sortDate || '')
  );

  // Filtrage par marque (brand)
  const filteredNews = filter === 'all'
    ? sortedNews
    : sortedNews.filter(item => item.brand === filter);


  // Configuration des filtres (Marques demandées)
  const brandFilters = [
    { id: 'all', label: t.newsPage.filters.all },
    { id: 'dell', label: t.newsPage.filters.dell },
    { id: 'kyocera', label: t.newsPage.filters.kyocera },
    { id: 'pny', label: t.newsPage.filters.pny },
    { id: 'vembu', label: t.newsPage.filters.vembu },
    { id: 'infortrend', label: t.newsPage.filters.infortrend }
  ];

  return (
    <div className="news-page">
      
      {/* 1. HERO SECTION */}
      <section className="news-hero-section">
        <ParticlesBackground />
        <Container className="text-center position-relative" style={{ zIndex: 10 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="hero-tag">{t.newsPage.hero.tag}</div>
            <h1 className="hero-title text-white" dangerouslySetInnerHTML={{ __html: t.newsPage.hero.title }}></h1>
            <p className="hero-subtitle text-muted-light">{t.newsPage.hero.subtitle}</p>
          </motion.div>
        </Container>
      </section>

      {/* 2. BARRE DE FILTRES PAR CONSTRUCTEUR */}
      <section className="filter-bar bg-white shadow-sm sticky-top">
        <Container>
          <div className="d-flex align-items-center justify-content-center flex-wrap gap-2 py-4">
            <div className="filter-label d-none d-lg-flex align-items-center me-3">
              <FaFilter className="me-2 text-danger" />
              <span>{lang === 'fr' ? 'CONSTRUCTEURS' : 'BRANDS'}</span>
            </div>
            {brandFilters.map((cat) => (
              <button 
                key={cat.id} 
                className={`btn-brand-filter ${filter === cat.id ? 'active' : ''}`}
                onClick={() => setFilter(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* 3. GRILLE DE NEWS */}
      <section className="py-5 bg-light min-vh-100">
        <Container>
          <Row className="g-4">
            <AnimatePresence mode='popLayout'>
              {filteredNews.map((item, index) => (
                <Col lg={4} md={6} key={item.id}>
                  <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                    <Card className="news-card-premium h-100 border-0 shadow-sm">
                      <div className="news-img-container">
                        <Card.Img 
                          variant="top" 
                          src={item.image} 
                          onError={(e) => { e.target.src = "https://via.placeholder.com/600x400?text=S2I+Partner+News"; }} 
                        />
                        <div className="news-category-tag">{item.category}</div>
                        {/* Petit badge pour identifier la marque */}
                        <div className="brand-badge-mini">{item.brand}</div>
                      </div>
                      <Card.Body className="p-4">
                        <div className="news-date mb-2"><FaRegCalendarAlt className="me-2" /> {item.date}</div>
                        <Card.Title className="news-card-title">{item.title}</Card.Title>
                        <Card.Text className="news-card-desc">{item.desc}</Card.Text>
                      </Card.Body>
                      <Card.Footer className="bg-transparent border-0 p-4 pt-0">
                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="news-read-more text-decoration-none d-inline-flex align-items-center">
                          {t.newsPage.readMore} <FaArrowRight className="ms-2" />
                        </a>
                      </Card.Footer>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </AnimatePresence>
          </Row>
        </Container>
      </section>

      <style>{`
        .news-page { background: #fdfdfd; min-height: 100vh; font-family: 'Poppins', sans-serif; }
        .news-hero-section { background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 140px 0 100px; position: relative; }
        .hero-title { font-size: 3.5rem; font-weight: 900; }
        .hero-title span { color: #d90000; }
        .hero-tag { display: inline-block; padding: 5px 20px; background: rgba(217,0,0,0.1); border: 1px solid #d90000; border-radius: 50px; color: #d90000; font-weight: 700; margin-bottom: 20px; }
        
        /* Filtres Constructeurs */
        .filter-bar { top: 110px; z-index: 1000; border-bottom: 1px solid #f0f0f0; }
        .filter-label { font-weight: 800; font-size: 0.7rem; letter-spacing: 2px; color: #1e293b; }
        .btn-brand-filter {
          background: #f8fafc; border: 1px solid #e2e8f0; padding: 10px 22px; border-radius: 12px;
          font-weight: 600; font-size: 0.85rem; color: #475569; transition: 0.3s;
        }
        .btn-brand-filter:hover { border-color: #d90000; color: #d90000; transform: translateY(-2px); }
        .btn-brand-filter.active { background: #d90000; border-color: #d90000; color: white; box-shadow: 0 4px 15px rgba(217, 0, 0, 0.2); }

        /* Cartes Premium */
        .news-card-premium { border-radius: 24px; transition: 0.4s; background: #fff; }
        .news-card-premium:hover { transform: translateY(-10px); box-shadow: 0 20px 40px rgba(0,0,0,0.08) !important; }
        .news-img-container { height: 230px; position: relative; overflow: hidden; border-radius: 24px 24px 0 0; }
        .news-img-container img { width: 100%; height: 100%; object-fit: cover; }
        .news-category-tag { position: absolute; top: 15px; right: 15px; background: #d90000; color: white; padding: 4px 14px; border-radius: 50px; font-size: 0.65rem; font-weight: 800; text-transform: uppercase; }
        .brand-badge-mini { position: absolute; bottom: 15px; left: 15px; background: rgba(15, 23, 42, 0.8); backdrop-filter: blur(4px); color: white; padding: 3px 12px; border-radius: 6px; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; }
        
        .news-card-title { font-weight: 700; font-size: 1.2rem; color: #1e293b; height: 3.5rem; overflow: hidden; }
        .news-card-desc { font-size: 0.85rem; color: #64748b; line-height: 1.6; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        .news-read-more { color: #d90000; font-weight: 700; transition: 0.3s; }
        .news-read-more:hover { color: #0f172a; transform: translateX(5px); }
        .news-date { font-size: 0.8rem; color: #d90000; font-weight: 600; }
      `}</style>
    </div>
  );
};

export default News;